// Autenticação do painel Super Master — login/logout/recuperação de sessão
// via /api/v1/platform-auth/*. Nunca fala com o Traccar nem com
// /api/v1/auth/* (sessão de empresa, ver gpsdrive-auth.service.ts): um
// PlatformAdmin tem senha própria, cadastrada só via
// prisma/seed-platform-admin.ts no backend, sem endpoint de auto-registro.
import { GPSDRIVE_API_URL } from "@/lib/api/gpsdrive-client";
import {
  PlatformApiError,
  platformPost,
  refreshPlatformSession,
  setPlatformAccessToken,
} from "@/lib/api/gpsdrive-platform-client";

export interface PlatformAdmin {
  id: string;
  name: string;
  email: string;
}

interface PlatformAuthResponse {
  accessToken: string;
  tokenType: "Bearer";
  expiresIn: number;
  admin: PlatformAdmin;
}

function requireBaseUrl(): string {
  if (!GPSDRIVE_API_URL) {
    throw new PlatformApiError("VITE_GPSDRIVE_API_URL não configurada", 0);
  }
  return GPSDRIVE_API_URL;
}

export const gpsDrivePlatformAuthService = {
  async login(email: string, password: string): Promise<PlatformAdmin> {
    const res = await fetch(`${requireBaseUrl()}/api/v1/platform-auth/login`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      const detail = await res.json().catch(() => undefined);
      throw new PlatformApiError(
        (detail as { message?: string })?.message ?? `Erro ${res.status} ao autenticar`,
        res.status,
      );
    }

    const data = (await res.json()) as PlatformAuthResponse;
    setPlatformAccessToken(data.accessToken);
    return data.admin;
  },

  // Mesmo espírito de gpsDriveAuthService.recoverSession() — usa o
  // single-flight de refreshPlatformSession, nunca um fetch próprio.
  async recoverSession(): Promise<PlatformAdmin | null> {
    const result = await refreshPlatformSession();
    return (result?.admin as PlatformAdmin | undefined) ?? null;
  },

  async logout(): Promise<void> {
    try {
      await fetch(`${requireBaseUrl()}/api/v1/platform-auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } finally {
      setPlatformAccessToken(null);
    }
  },

  // Backend revoga todo refresh token ao trocar a senha (ver
  // platform-auth.controller.ts) — o cookie local já fica inválido depois
  // desta chamada, então o caller sempre navega pra /master/login em seguida
  // (ver ChangePasswordDialog).
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await platformPost<void>("/api/v1/platform-auth/change-password", { currentPassword, newPassword });
    setPlatformAccessToken(null);
  },
};
